/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { FaRegCalendarAlt } from '@react-icons/all-files/fa/FaRegCalendarAlt';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaChevronCircleRight } from '@react-icons/all-files/fa/FaChevronCircleRight';
import { GiTennisRacket } from '@react-icons/all-files/gi/GiTennisRacket';
import { GiDart } from '@react-icons/all-files/gi/GiDart';
import { GiSportMedal } from '@react-icons/all-files/gi/GiSportMedal';
import { GiSoccerBall } from '@react-icons/all-files/gi/GiSoccerBall';
import { translate, prettyTracker } from 'gatsby-core-theme/src/helpers/getters';
import LazyImage from 'gatsby-core-theme/src/hooks/lazy-image';
import Button from 'gatsby-core-theme/src/components/atoms/button';
import { Context } from '~context/MainProvider';
import RegistrationSteps from '../../registration-steps';
import { getMarketCountries } from '../../../../helpers/getters';
import { isValidEventDate } from '../../../../helpers/events';
import styles from './game-box.module.scss';

/* eslint-disable react/jsx-one-expression-per-line, react/jsx-wrap-multilines */

const GameBox = ({ section, category }) => {
  const { translations } = useContext(Context) || {};
  const relation = section?.page?.relation;
  const data = relation?.event;
  const sport = category;
  // check if there is a main provider or not
  const mainProvider = data?.providers.filter((item) => item.main === 1)[0];
  // if there is not a main provider the first item is the event provider
  const eventProvider = mainProvider || data?.providers[0];

  const isMainProvider = eventProvider?.main === 1;
  const showTnC = eventProvider?.sports_data[sport]?.disclaimer !== null;

  const getFallbackImg = () => {
    switch (sport) {
      case 'handball':
        return <GiSportMedal color="#fff" size={65} className={styles.replacementIcon} />;
      case 'soccer':
        return <GiSoccerBall color="#fff" size={65} className={styles.replacementIcon} />;
      case 'tennis':
        return <GiTennisRacket color="#fff" size={65} className={styles.replacementIcon} />;
      case 'darts':
        return <GiDart color="#fff" size={65} className={styles.replacementIcon} />;
      default:
        return null;
    }
  };

  const isMarketProvider = getMarketCountries(section?.page?.market).includes(
    data?.tournament?.country
  );

  const time = data?.date?.time.split(':', 2);
  const eventDate = new Date(
    data?.date?.year_number,
    data?.date?.month_number - 1,
    data?.date?.day_number,
    time && time[0],
    time && time[1]
  );
  const targetDate = eventDate.getTime();

  const isValidDate = isValidEventDate(data?.date);
  const qualifyForBet = translate(
    translations,
    'qualify_for_bet',
    'To qualify, you simply need to have a funded account or have placed a bet within the last 24 hours.'
  );

  const TnCModal = showTnC ? loadable(() => import('gatsby-core-theme/src/hooks/modal')) : <></>;

  const DateCountDown = isValidDate ? (
    loadable(() =>
      import('gatsby-core-theme/src/components/molecules/counter/counter-item/count-date')
    )
  ) : (
    <></>
  );

  return (
    <div className={styles.gameBox}>
      <p className={styles.sponsored}>{translate(translations, 'sponsored', 'Sponsored')}</p>
      <h2>
        {`${translate(translations, 'how_to_stream', 'How to stream')} ${relation?.sport?.path}`}
      </h2>
      {isMainProvider && (
        <p className={styles.deposits}>
          {translate(
            translations,
            'take_back_your_deposits_at_any_time',
            'Withdraw your deposit at any time'
          )}
        </p>
      )}
      <div className={styles.teams}>
        <span className={styles.homeTeamName}>{data?.home_team?.name}</span>
        <LazyImage
          alt={data?.home_team?.name}
          src={data?.home_team?.logo}
          width={65}
          height={65}
          defaultImg={getFallbackImg()}
        />
        <span className={styles.vs}>VS</span>
        <span className={styles.awayTeamName}>{data?.away_team?.name}</span>
        <LazyImage
          alt={data?.away_team?.name}
          src={data?.away_team?.logo}
          width={65}
          height={65}
          defaultImg={getFallbackImg()}
        />
      </div>
      <div className={styles.dateTime}>
        <span>
          <FaRegCalendarAlt />
          {`${data?.date?.day_number}. ${data?.date?.month_short} ${data?.date?.year}`}
        </span>
        <span>
          <FaRegClock />
          {translate(translations, 'at', 'at')} {data?.date?.time}
        </span>
      </div>
      <div className={styles.steps}>
        <RegistrationSteps
          sport={sport}
          steps={eventProvider}
          info={isMainProvider ? `* ${qualifyForBet}` : null}
          imagePath={null}
          provider={eventProvider}
          pageType={section.page.relation_type}
          isGamebox
          gtmClass="game-box-registration-steps-gtm"
        />
      </div>
      {isValidDate && (
        <div className={styles.countDown}>
          <p>{translate(translations, 'match_start', 'Match start')}:</p>
          <DateCountDown targetDate={targetDate} />
        </div>
      )}
      <Button
        to={prettyTracker(eventProvider, 'main', true)}
        className={isMainProvider ? 'primary' : sport}
        isInternalLink={false}
        targetBlank={false}
        btnText={
          eventProvider?.name ? (
            <>
              <FaChevronCircleRight />
              {translate(translations, 'watch_live_at', 'Watch live at')}
              {` ${eventProvider?.name}`}
            </>
          ) : (
            <>
              <FaChevronCircleRight />
              {translate(translations, 'watch_live_online', 'Watch live online')}
            </>
          )
        }
        gtmClass="game-box-gtm btn-cta"
      />
      <div className={styles.terms}>
        {isMainProvider && eventProvider?.sports_data[sport]?.disclaimer && (
          <TnCModal content={eventProvider?.sports_data[sport]?.disclaimer}>
            <span className={styles.modalTrigger}>
              {translate(translations, 'terms_and_conditions', 'Terms and conditions')}
            </span>
          </TnCModal>
        )}
        {isMainProvider && isMarketProvider && (
          <span className={styles.ipProvider}>
            <FaExclamationCircle />
            {translate(
              translations,
              'not_possible_with_ip_address',
              'Not possible with this IP address'
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default GameBox;

GameBox.propTypes = {
  section: PropTypes.shape({
    page: PropTypes.shape({
      market: PropTypes.string,
      relation_type: PropTypes.string,
      type: PropTypes.string,
      sections: PropTypes.shape({
        header: PropTypes.shape({}),
      }),
      relation: PropTypes.shape({
        event: PropTypes.shape({}),
        sport: PropTypes.shape({}),
      }),
    }),
  }).isRequired,
  category: PropTypes.string,
};
